import { defineStore } from 'pinia'
import { queryClient } from '@/queryClient.js'
import {
	RESOURCE_NAMES,
	resourceSchema,
	INFLUXDB,
	readResourceItem, updateResourceItem, readResources,
	updateStationMetrics,
	updateUnitsLastConnectTime,
	updateStationMetricsStartValues,
	updateMetricLastTime,
} from '@/api/api.js'
import { Station } from '@/models/station.js'
import { ServiceUnit } from '@/models/serviceUnit.js'
import { Breaker } from '@/models/breaker.js'


const _passportRequestArguments = [
	`embedded={
		"${Station.passportKeys.orgId}":1,
		"${Station.passportKeys.stabilization.self}.${Station.passportKeys.stabilization.actor}":1,
		"${Station.passportKeys.creatorId}":1,
		"${Station.passportKeys.updateBy}":1,
		"${Station.passportKeys.pipelineId}":1
	}`
]


export const useStationsStore = defineStore('stationsStore', {
    state: () => ({
		// Station passports
		stations: new Map(),
		// Organization ID for filtering station
		organizationId: null,
		// Departments ID for filtering station
		departmentsId: [],
		// Sectors ID for filtering station
		sectorsId: [],
		// Access keys for rest API (mongoDB)
		_api: {
			login: null,
			password: null,
		},
	}),

    getters: {
        isNoStations() {
			return this.stations.size === 0
		},

		stationsArray() {
			return Array.from(this.stations.values())
		},
    },

    actions: {
		setOrganizationId(orgId=null) {
			this.organizationId = orgId
		},

		setDepartmnetsId(id=[]) {
			this.departmentsId = id
		},

		setSectorsId(id=[]) {
			this.sectorsId = id
		},

		setApiAccessKeys(login, password) {
			this._api.login = login
			this._api.password = password
		},

		station(id) { return this.stations.get(id) },

		createStation(passport, settings) {
			console.log(`Create station. Passport ${passport},\nsettings ${settings}`)
		},

		// Read all stations passports to component stations Map
		async readStations(handler) {
			// console.log("readStations")
			let args = [ ... _passportRequestArguments ]
			let departments = await this._readOrgDepartments()
			const minute = 60 * 1000
			const hour = 60 * 60 * 1000
			
			let filters = []
			// If organization id is set
			if (this.organizationId !== null) {
				// Add filter
				filters.push(`"${Station.passportKeys.orgId}": "${this.organizationId}"`)				
			}

			// If user departments is set
			if (this.departmentsId.length > 0) {
				// Add filters
				// Example:
				// 	"$or": [
				// 		{"department_id": {"$in": ["***", "***"]}}, // add stations with department_id == ***
				// 		{"department_id": null} // add stations witout department_id
				// 	],
				let filter = `"$or":[{"${Station.passportKeys.departmentId}": {"$in": [`
				for (const id of this.departmentsId) {
					filter += `"${id}",`
				}
				filter = filter.slice(0, -1); 
				filter += ']}},'
				filter += `{"${Station.passportKeys.departmentId}": null}]`
				filters.push(filter)
			}

			// If user sectors is set
			if (this.sectorsId.length > 0) {
				// Add filters
				let filter = `"$or":[{"${Station.passportKeys.sectorId}": {"$in": [`
				for (const id of this.sectorsId) {
					filter += `"${id}",`
				}
				filter = filter.slice(0, -1); 
				filter += ']}},'
				filter += `{"${Station.passportKeys.sectorId}": null}]`
				filters.push(filter)
			}

			// If any filters is set
			if (filters.length > 0) {
				// Add all filters to request arguments
				let arg = 'where={'
				for (const filter of filters) {
					arg += `${filter},`
				}
				arg = arg.slice(0, -1); 
				arg += '}'
				
				args.push(arg)
			}

			try {
				const passports = await queryClient.fetchQuery({
					queryKey: [RESOURCE_NAMES.stationPassports, ...args],
					queryFn: () => readResources(
						RESOURCE_NAMES.stationPassports,
						this._api.login,
						this._api.password,
						args
					),
					staleTime: hour,
					gcTime: minute,
				})

				// Append only new stations and remove old stations
				//
				const curentStationsId = new Set([...this.stations.keys()])
				const newStationsId = new Set(passports.map((passport) => passport[resourceSchema.id.key]))

				// Add new stations
				passports.forEach(passport => {
					const station = new Station(passport[resourceSchema.id.key])
					this._updateStationPassportInStorage(station, passport, departments)
					
					if (!curentStationsId.has(passport[resourceSchema.id.key])) {
						// Save new station Object
						this.stations.set(passport[resourceSchema.id.key], station)
					}
				})

				// Remove old station objects
				curentStationsId.difference(newStationsId).forEach((id) => this.stations.delete(id))

				if (handler) { handler(true) }
			} catch (err) {
				console.error("Error on read station passports: ", err, ".")
				if (handler) { handler(false) }
			}
		},

		/* Read metrics and save to state this.stations
		* handler: function calls after fetch (result:boolean) => {}
		* stationId: if present - read only station with stationId
		*/
		async readMetrics(handler, stationId) {
			const minute = 60 * 1000
			const hour = 60 * 60 * 1000
			const day = 24 * 60 * 60 * 1000
			let stations = undefined
			let isUpdate = false
			let allStationsId = []
			// console.time('StationsStore_readMetrics')

			if (stationId) {
				if (!this.stations.has(stationId)) {
					await this.readStations()
				}

				stations = new Map([[stationId, this.stations.get(stationId)]])
				allStationsId = [...stations.keys()]
			} else {
				stations = this.stations
				allStationsId = [...stations.keys()]
			}

			// console.log('StationsStore_readMetrics begin', allStationsId)

			try {
				isUpdate = await queryClient.fetchQuery({
					queryKey: ['updateStationMetrics', ...allStationsId],
					queryFn: () => updateStationMetrics(stations),
					staleTime: minute,
					gcTime: minute,
				})
			} catch (err) {
				console.error("Error on read stations metrics: ", err, ".")
				if (handler) { handler(false) }
			}
			if (!isUpdate) {
				return
			}

			try {
				isUpdate = await queryClient.fetchQuery({
					queryKey: ['unitsLastConnectTime', ...allStationsId],
					queryFn: () => updateUnitsLastConnectTime(stations),
					gcTime: minute, // The time in milliseconds that unused/inactive cache data remains in memory
				})
			} catch (err) {
				console.error("Error on read last connection time: ", err, ".")
				if (handler) { 
					handler(false) 
				}
			}
			if (!isUpdate) {
				return
			}

			try {
				isUpdate = await queryClient.fetchQuery({
					queryKey: ['boxDoorLastOpenTime', stations],
					queryFn: () => updateMetricLastTime(
						stations,
						'boxDoorLastOpenTime',
						INFLUXDB.station_metrics.service_unit,
						ServiceUnit.metricKeys.fSecurityCircuitBoxDoor,
						2,
						'1y',
					),
					staleTime: 10 * minute,
					gcTime: hour,
				})
			} catch (err) {
				console.error("Error on read box door opened last time: ", err, ".")
				if (handler) { handler(false) }
			}
			if (!isUpdate) {
				return
			}

			try {
				isUpdate = await queryClient.fetchQuery({
					queryKey: ['boxDoorLastCloseTime', stations],
					queryFn: () => updateMetricLastTime(
						stations,
						'boxDoorLastCloseTime',
						INFLUXDB.station_metrics.service_unit,
						ServiceUnit.metricKeys.fSecurityCircuitBoxDoor,
						1,
						'1y',
					),
					staleTime: 10 * minute,
					gcTime: hour,
				})
			} catch (err) {
				console.error("Error on read box door closed last time: ", err, ".")
				if (handler) { handler(false) }
			}
			if (!isUpdate) {
				return
			}

			// Breaker metrics
			const breakerSpecificMetrics = [
				{
					key: 'fBreakerActiveLast',
					rawMetricKey: Breaker.metricKeys.fBreakerActive,
					value: false,
				},
				{
					key: 'fEepromErrorLast',
					rawMetricKey: Breaker.metricKeys.fEepromError,
					value: false,
				},{
					key: 'fServiceUnitConnectionErrorLast',
					rawMetricKey: Breaker.metricKeys.fServiceUnitConnectionError,
					value: false,
				},{
					key: 'fPowerUnitConnectionErrorLast',
					rawMetricKey: Breaker.metricKeys.fPowerUnitConnectionError,
					value: false,
				},{
					key: 'fGpsDataOkLast',
					rawMetricKey: Breaker.metricKeys.fGpsDataOk,
					value: true,
				},
			]
			for (const metric of breakerSpecificMetrics) {
				try {
					isUpdate = await queryClient.fetchQuery({
						queryKey: ['metricLastTime', metric.key, stations],
						queryFn: () => updateMetricLastTime(
							stations,
							metric.key,
							INFLUXDB.station_metrics.breaker,
							metric.rawMetricKey,
							metric.value,
							'1y',
						),
						staleTime: 10 * minute,
						gcTime: hour,
					})
				} catch (err) {
					console.error("Error on read breaker metric last time: ", err, ".")
					if (handler) { handler(false) }
				}
				if (!isUpdate) {
					return
				}
			}

			if (stationId) {
				try {
					isUpdate = await queryClient.fetchQuery({
						queryKey: ['updateStationMetricsStartValues', stationId],
						queryFn: () => updateStationMetricsStartValues(this.stations.get(stationId)),
						staleTime: hour,
						gcTime: day,
					})
				} catch (err) {
					console.error("Error on read stations metrics start value: ", err, ".")
					if (handler) { handler(false) }
				}
				if (!isUpdate) {
					handler(isUpdate)
				}
			} else {
				isUpdate = true
			}

			if (handler) {
				handler(isUpdate)
			}

			// console.timeEnd('StationsStore_readMetrics')
		},

		/* Read passport and save to state this.stations
		* handler: function calls after fetch (result:boolean) => {}
		* stationId: if present - read only station with stationId
		*/
		async readPassport(handler, stationId) {
			if (stationId) {
				let args = [ ... _passportRequestArguments ]
				let departments = await this._readOrgDepartments()

				// if (this.stations.length == 0) {
				// 	await this.readStations()
				// }

				// Read one station passport
				readResourceItem(
					RESOURCE_NAMES.stationPassports,
					stationId,
					this._api.login,
					this._api.password,
					args
				)
				.then(response => {
					//console.log("response", response)
					let station = this.stations.get(response[resourceSchema.id.key])
					if (!station) {
						station = new Station(response[resourceSchema.id.key])
					}

					//station.passport = new Map(Object.entries(response))
					this._updateStationPassportInStorage(station, response, departments)
					
					// If no station in storage
					if (!this.stations.has(response[resourceSchema.id.key])) {
						// Save station Object
						this.stations.set(response[resourceSchema.id.key], station)
					}

					if (handler) {
						handler(true)
					}
				}).catch(error => {
					console.error(`Error on read passport: "${error}".`)
					if (handler) {
						handler(false)
					}
				})
			} else {
				// Read passports for all stations
			}
		},

		/* Update passport and save to state this.stations
		* handler: function calls after fetch (result:boolean) => {}
		* stationId: update station with stationId
		* payload: Object updated passport fields
		*/
		updatePasport(handler, stationId, payload) {
			// Update one station passport
			updateResourceItem(
				RESOURCE_NAMES.stationPassports,
				this._api.login,
				this._api.password,
				this.station(stationId).passport.get("_id"),
				this.station(stationId).passport.get("_etag"),
				payload
			).then(result => {
				if (result._status == 'OK') {
					//console.log("Паспорт записано успіно.")
					this.station(stationId).passport.set("_etag", result._etag)
					this.station(stationId).passport.set("_updated", result._updated)

					// Update 
					for (const [key, value] of Object.entries(payload)) {
						this.station(stationId).passport.set(key, value)
					}

					if (handler) {
						handler(true)
					}
				} else {
					if (handler) {
						handler(false)
					}
				}
			}).catch(err => {
				console.error("on update passport", err)
				if (handler) {
					handler(false)
				}
			})
		},

		// delete ?

		/*
		Reads current organization departments or all organizations departments
		depends on this.organizationId.
		Returns Map  orgId: departments_object
		*/
		async _readOrgDepartments() {
			let departments = new Map() // orgId: departments
			
			// If organization id is set
			if (this.organizationId !== null) {
				// Read organization
				const result = await readResourceItem(
					RESOURCE_NAMES.organizations,
					this.organizationId,
					this._api.login,
					this._api.password,
				).then(response => {
					return response
				}).catch((err) => {
					console.error("on read organization departments", err)
					return null
				})

				if (result) {
					departments.set(result[resourceSchema.id.key], result.departments)
				}
			} else {
				// Read all organizations
				const result = await readResources(
					RESOURCE_NAMES.organizations,
					this._api.login,
					this._api.password,
				)

				result.forEach((org) => {
					departments.set(org[resourceSchema.id.key], org.departments)
				})
			}

			return departments
		},


		/*
		Updates station object with new passport
		*/
		async _updateStationPassportInStorage(station, passport, departments) {
			// Write passport fields to station passport
			Object.entries(passport).forEach(([key, value]) => {
				if (key == Station.passportKeys.orgId) {
					station.passport.set(Station.passportKeys.org, value)
					station.passport.set(Station.passportKeys.orgId, value[resourceSchema.id.key])
					// Add organization name to passport (copy from organization object)
					station.passport.set(Station.passportKeys.orgName, value.title)
				} else {
					station.passport.set(key, value)
				}
			})

			// Add department object to station
			const orgId = station.passport.get(Station.passportKeys.orgId)
			const orgDeps = departments.get(orgId)
			let curStationDepartment = undefined
			// If all stations in same organization
			if (orgDeps) {
				curStationDepartment = orgDeps.find(
					dep => dep.id == station.passport.get(Station.passportKeys.departmentId)
				)

				station.passport.set(
					Station.passportKeys.department,
					curStationDepartment
				)
			}

			// Add sector object to station
			const curStationSectorId = station.passport.get(Station.passportKeys.sectorId)
			if (curStationSectorId && curStationDepartment && curStationDepartment.sectors) {
				const curStationSector = curStationDepartment.sectors.find(
					sec => sec.id == curStationSectorId
				)
				station.passport.set(
					Station.passportKeys.sector,
					curStationSector
				)
			}
		},
    },
})
